import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  getStoredSessionToken,
  getSupportHours,
  getWebChatResumeInfo,
  pollWebChatSession,
  rateWebChatSession,
  resumeWebChat,
  resumeWebChatWithAccount,
  resumeWebChatWithPin,
  sendWebChatMessage,
  setStoredSessionToken,
  startWebChat,
  type WebChatMessage,
  type WebChatSession,
} from '../../api/web-chat';
import { ApiRequestError } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useWebChatSocket } from '../../hooks/useWebChatSocket';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { onChatEnterKey } from '../../utils/chat-composer';
import './chat-widget.css';

const CLOSED_STATUSES = new Set(['awaiting_customer_approval', 'closed', 'cancelled', 'resolved']);

function formatTime(value: string) {
  return new Date(value).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

function isActiveStatus(status: string | null): boolean {
  return !!status && !CLOSED_STATUSES.has(status);
}

function mergeMessages(prev: WebChatMessage[], incoming: WebChatMessage[]): WebChatMessage[] {
  if (incoming.length === 0) return prev;
  const ids = new Set(prev.map((m) => m.id));
  const merged = [...prev];
  for (const msg of incoming) {
    if (!ids.has(msg.id)) merged.push(msg);
  }
  return merged;
}

function MessageBubble({ message }: { message: WebChatMessage }) {
  const className =
    message.sender_type === 'customer'
      ? 'chat-bubble chat-bubble--customer'
      : message.sender_type === 'system'
        ? 'chat-bubble chat-bubble--system'
        : 'chat-bubble chat-bubble--agent';

  return (
    <div className={className}>
      {message.content}
      {message.sender_type !== 'system' ? (
        <span className="chat-bubble__time">{formatTime(message.created_at)}</span>
      ) : null}
    </div>
  );
}

interface ChatWidgetProps {
  onClose: () => void;
}

export function ChatWidget({ onClose }: ChatWidgetProps) {
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const [messages, setMessages] = useState<WebChatMessage[]>([]);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [initialMessage, setInitialMessage] = useState('');
  const [outsideHours, setOutsideHours] = useState(false);
  const [markUrgent, setMarkUrgent] = useState(false);
  const [startError, setStartError] = useState('');
  const [needsCard, setNeedsCard] = useState(false);
  const [resumeMode, setResumeMode] = useState<'login' | 'pin' | null>(null);
  const [resumeTicketId, setResumeTicketId] = useState<number | null>(null);
  const [pinInput, setPinInput] = useState('');
  const [resumeError, setResumeError] = useState('');
  const [savedGuestPin, setSavedGuestPin] = useState<string | null>(null);
  const [awaitingRating, setAwaitingRating] = useState(false);
  const [customerRating, setCustomerRating] = useState<string | null>(null);
  const [ratingFeedback, setRatingFeedback] = useState('');
  const [ratingHover, setRatingHover] = useState<number | null>(null);
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const lastMessageIdRef = useRef<number | undefined>(undefined);

  const applySession = useCallback((session: WebChatSession) => {
    setSessionToken(session.sessionToken);
    setStoredSessionToken(session.sessionToken);
    setMessages(session.messages);
    setStatus(session.status);
    setAwaitingRating(Boolean(session.awaitingRating));
    setCustomerRating(session.customerRating ?? null);
    lastMessageIdRef.current = session.messages.at(-1)?.id;
  }, []);

  const clearSession = useCallback(() => {
    setSessionToken(null);
    setStatus(null);
    setMessages([]);
    setAwaitingRating(false);
    setCustomerRating(null);
    setRatingFeedback('');
    setRatingHover(null);
    setStoredSessionToken(null);
    lastMessageIdRef.current = undefined;
  }, []);

  const syncSession = useCallback(
    async (token: string, sinceId?: number) => {
      const poll = await pollWebChatSession(token, sinceId);
      if (poll) {
        setStatus(poll.status);
        setAwaitingRating(Boolean(poll.awaitingRating));
        setCustomerRating(poll.customerRating ?? null);
        if (poll.messages.length > 0) {
          setMessages((prev) => {
            const merged = mergeMessages(prev, poll.messages);
            lastMessageIdRef.current = merged.at(-1)?.id;
            return merged;
          });
        }
        return poll;
      }

      const resumed = await resumeWebChat(token);
      if (resumed) {
        if (sinceId) {
          setStatus(resumed.status);
          setAwaitingRating(Boolean(resumed.awaitingRating));
          setCustomerRating(resumed.customerRating ?? null);
          const newMessages = resumed.messages.filter((m) => m.id > (sinceId ?? 0));
          if (newMessages.length > 0) {
            setMessages((prev) => {
              const merged = mergeMessages(prev, newMessages);
              lastMessageIdRef.current = merged.at(-1)?.id;
              return merged;
            });
          }
        } else {
          applySession(resumed);
        }
        return {
          messages: resumed.messages,
          status: resumed.status,
          isActive: resumed.isActive,
        };
      }

      clearSession();
      return null;
    },
    [applySession, clearSession]
  );

  useEffect(() => {
    if (user?.email && !email) {
      setEmail(user.email);
    }
    if (user?.name && !name) {
      setName(user.name);
    }
  }, [user, email, name]);

  // Consultar el horario de soporte mientras estamos en la pantalla de inicio,
  // para ofrecer "marcar como urgente" solo fuera de horario.
  useEffect(() => {
    if (sessionToken) return;
    let cancelled = false;
    void getSupportHours().then((info) => {
      if (!cancelled) setOutsideHours(info.outsideHours);
    });
    return () => {
      cancelled = true;
    };
  }, [sessionToken]);

  const tryResumeFromTicket = useCallback(
    async (ticketId: number) => {
      setResumeError('');
      const info = await getWebChatResumeInfo(ticketId);
      if (!info) {
        setResumeError('No hemos encontrado esta conversación.');
        setResumeMode(null);
        return false;
      }
      if (!info.canResume) {
        setResumeError('Esta conversación ya está cerrada.');
        setResumeMode(null);
        return false;
      }

      if (info.requiresLogin) {
        if (!isAuthenticated) {
          setResumeTicketId(ticketId);
          setResumeMode('login');
          return false;
        }
        const session = await resumeWebChatWithAccount(ticketId);
        if (session) {
          applySession(session);
          setResumeMode(null);
          setResumeTicketId(null);
          return true;
        }
        setResumeError('No pudimos vincular la conversación a tu cuenta.');
        return false;
      }

      if (info.requiresPin) {
        setResumeTicketId(ticketId);
        setResumeMode('pin');
        return false;
      }

      setResumeError('No se puede reanudar esta conversación.');
      return false;
    },
    [applySession, isAuthenticated]
  );

  useEffect(() => {
    if (authLoading) return;

    let cancelled = false;

    async function load() {
      const ticketParam = searchParams.get('ticket');
      const ticketId = ticketParam ? Number(ticketParam) : NaN;

      if (Number.isFinite(ticketId) && ticketId > 0) {
        const resumed = await tryResumeFromTicket(ticketId);
        if (cancelled) return;
        setLoading(false);
        if (resumed) return;
        return;
      }

      const token = getStoredSessionToken();
      if (!token) {
        if (!cancelled) setLoading(false);
        return;
      }

      const session = await resumeWebChat(token);
      if (cancelled) return;

      if (session) {
        applySession(session);
      } else {
        clearSession();
      }
      setLoading(false);
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [applySession, authLoading, clearSession, searchParams, tryResumeFromTicket]);

  useEffect(() => {
    if (authLoading || !resumeMode || resumeMode !== 'login' || !resumeTicketId) return;
    if (!isAuthenticated) return;

    void (async () => {
      setLoading(true);
      const session = await resumeWebChatWithAccount(resumeTicketId);
      if (session) {
        applySession(session);
        setResumeMode(null);
        setResumeTicketId(null);
      } else {
        setResumeError('No pudimos recuperar la conversación con tu cuenta.');
      }
      setLoading(false);
    })();
  }, [applySession, authLoading, isAuthenticated, resumeMode, resumeTicketId]);

  // Tiempo real vía socket (sin polling). El socket solo conecta mientras la
  // conversación está activa o esperando valoración; al conectar y al cambiar de
  // estado se hace un resync puntual para recuperar mensajes de sistema.
  const shouldStream = !!sessionToken && (isActiveStatus(status) || awaitingRating);
  useWebChatSocket(sessionToken, shouldStream, {
    onConnect: () => {
      if (sessionToken) void syncSession(sessionToken, lastMessageIdRef.current);
    },
    onMessage: (message) => {
      setMessages((prev) => {
        const merged = mergeMessages(prev, [message]);
        lastMessageIdRef.current = merged.at(-1)?.id;
        return merged;
      });
    },
    onStatus: (event) => {
      setStatus(event.status);
      setAwaitingRating(event.awaitingRating);
      setCustomerRating(event.customerRating);
      if (sessionToken) void syncSession(sessionToken, lastMessageIdRef.current);
    },
  });

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  async function submitStart() {
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      return;
    }
    if (!initialMessage.trim() || sending) return;

    setSending(true);
    setStartError('');
    setNeedsCard(false);
    try {
      const session = await startWebChat({
        message: initialMessage.trim(),
        customerName: name.trim() || undefined,
        customerEmail: trimmedEmail,
        urgent: outsideHours && markUrgent,
      });
      applySession(session);
      if (session.guestPin) {
        setSavedGuestPin(session.guestPin);
      }
      setInitialMessage('');
      setMarkUrgent(false);
    } catch (err) {
      if (err instanceof ApiRequestError && err.code === 'needs_card') {
        setNeedsCard(true);
        setStartError('No se pudo iniciar el chat. Si necesitas una intervención técnica, podrás autorizarla antes de que se cobre.');
      } else {
        setStartError(err instanceof Error ? err.message : 'No se pudo iniciar el chat');
      }
    } finally {
      setSending(false);
    }
  }

  async function submitPinResume(event: FormEvent) {
    event.preventDefault();
    if (!resumeTicketId || pinInput.replace(/\D/g, '').length !== 6) return;

    setSending(true);
    setResumeError('');
    try {
      const session = await resumeWebChatWithPin(resumeTicketId, pinInput);
      if (!session) {
        setResumeError('Código incorrecto. Revisa el correo de asignación del ticket.');
        return;
      }
      applySession(session);
      setResumeMode(null);
      setResumeTicketId(null);
      setPinInput('');
    } finally {
      setSending(false);
    }
  }

  const loginRedirect = resumeTicketId
    ? `/login?redirect=${encodeURIComponent(`/contacto?soporte=1&ticket=${resumeTicketId}`)}`
    : '/login';

  async function submitMessage() {
    if (!sessionToken || !draft.trim() || sending || !isActiveStatus(status)) return;

    const content = draft.trim();
    setDraft('');
    setSending(true);
    try {
      const message = await sendWebChatMessage(sessionToken, content);
      setMessages((prev) => {
        const merged = mergeMessages(prev, [message]);
        lastMessageIdRef.current = merged.at(-1)?.id;
        return merged;
      });
    } catch {
      setDraft(content);
      await syncSession(sessionToken, lastMessageIdRef.current);
    } finally {
      setSending(false);
    }
  }

  function handleStart(event: FormEvent) {
    event.preventDefault();
    void submitStart();
  }

  function handleSend(event: FormEvent) {
    event.preventDefault();
    void submitMessage();
  }

  function startNewConversation() {
    clearSession();
    setDraft('');
    setInitialMessage('');
    setMarkUrgent(false);
    setLoading(false);
  }

  async function submitRating(rating: string) {
    if (!sessionToken || ratingSubmitting || customerRating) return;

    setRatingSubmitting(true);
    try {
      const session = await rateWebChatSession(
        sessionToken,
        rating,
        ratingFeedback.trim() || undefined
      );
      applySession(session);
      setRatingFeedback('');
    } finally {
      setRatingSubmitting(false);
    }
  }

  const isActive = sessionToken && isActiveStatus(status);
  const showAgentBanner = status === 'assigned' || status === 'in_progress';
  const showResolvedBanner = sessionToken && status === 'resolved' && !awaitingRating && customerRating;
  const showRatingPrompt = sessionToken && awaitingRating && !customerRating;
  const showClosedBanner =
    sessionToken && (status === 'closed' || status === 'cancelled');

  return (
    <div className="chat-widget" role="dialog" aria-label="Chat de soporte">
      <div className="chat-widget__header">
        <div>
          <h3>Soporte Hosty</h3>
          <p>Te respondemos desde aquí, sin WhatsApp</p>
        </div>
        <button type="button" className="chat-widget__close" onClick={onClose} aria-label="Cerrar chat">
          ×
        </button>
      </div>

      {savedGuestPin ? (
        <div className="chat-widget__status chat-widget__status--pin">
          Código para otro dispositivo: <strong>{savedGuestPin}</strong>
        </div>
      ) : null}

      {showAgentBanner ? (
        <div className="chat-widget__status">Un agente está atendiendo tu consulta</div>
      ) : null}

      {showResolvedBanner ? (
        <div className="chat-widget__status">Gracias por valorar la atención recibida</div>
      ) : showRatingPrompt ? (
        <div className="chat-widget__status">Tu consulta ha sido resuelta — valora al agente</div>
      ) : sessionToken && status === 'resolved' && customerRating ? (
        <div className="chat-widget__status">Tu consulta ha sido resuelta</div>
      ) : null}

      <div className="chat-widget__body" ref={bodyRef}>
        {loading ? (
          <p className="chat-widget__intro">Cargando conversación…</p>
        ) : resumeMode === 'login' ? (
          <div className="chat-widget__intro">
            <p>Esta conversación está vinculada a una cuenta Hosty. Inicia sesión para continuar.</p>
            {resumeError ? <p className="chat-widget__error">{resumeError}</p> : null}
            <Link to={loginRedirect} className="ui-btn ui-btn--primary ui-btn--md">
              Iniciar sesión
            </Link>
          </div>
        ) : resumeMode === 'pin' ? (
          <form className="chat-widget__intro" onSubmit={(e) => void submitPinResume(e)}>
            <p>
              Introduce el código de 6 dígitos que te enviamos por correo al asignar tu consulta.
            </p>
            <Input
              label="Código de acceso"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
              required
            />
            {resumeError ? <p className="chat-widget__error">{resumeError}</p> : null}
            <Button type="submit" disabled={sending || pinInput.length !== 6}>
              Continuar conversación
            </Button>
          </form>
        ) : !sessionToken ? (
          isAuthenticated ? (
            <div className="chat-widget__intro">
              <p>
                Crea o selecciona una solicitud desde la página de soporte para abrir su conversación.
              </p>
              <Link to="/panel/soporte" className="ui-btn ui-btn--primary ui-btn--md" onClick={onClose}>
                Ir a soporte
              </Link>
            </div>
          ) : (
          <form className="chat-widget__intro" onSubmit={handleStart}>
            <p>
              Escríbenos y un agente te responderá por este chat o por correo. Tu conversación se
              guarda para que puedas continuar más tarde. Las preguntas y el chat básico son gratuitos;
              si hiciera falta una intervención técnica, te enviaremos antes una propuesta para autorizarla.
            </p>
            <Input label="Nombre (opcional)" value={name} onChange={(e) => setName(e.target.value)} />
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <div className="ui-field">
              <label className="ui-field__label" htmlFor="chat-initial-message">
                Tu mensaje
              </label>
              <textarea
                id="chat-initial-message"
                className="ui-input"
                value={initialMessage}
                onChange={(e) => setInitialMessage(e.target.value)}
                onKeyDown={(e) => onChatEnterKey(e, () => void submitStart())}
                rows={3}
                required
                placeholder="¿En qué podemos ayudarte?"
              />
            </div>
            {outsideHours ? (
              <div className="chat-widget__offhours">
                <p className="chat-widget__offhours-note">
                  Estás escribiendo fuera de nuestro horario de atención (L-V, 9:00–18:00). Te
                  responderemos en cuanto volvamos a abrir.
                </p>
                <label className="chat-widget__urgent">
                  <input
                    type="checkbox"
                    checked={markUrgent}
                    onChange={(e) => setMarkUrgent(e.target.checked)}
                  />
                  <span>
                    <strong>Marcar como urgente</strong>
                    <small>Solo si es una incidencia que no puede esperar a nuestro próximo horario.</small>
                  </span>
                </label>
              </div>
            ) : null}
            {startError ? (
              <p className="chat-widget__error">
                {startError}
                {needsCard ? (
                  <>
                    {' '}
                    <Link to="/panel/soporte">Añadir método de pago</Link>
                  </>
                ) : null}
              </p>
            ) : null}
            <Button
              type="submit"
              disabled={
                sending ||
                !initialMessage.trim() ||
                !email.trim() ||
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
              }
            >
              Iniciar chat
            </Button>
          </form>
          )
        ) : messages.length === 0 ? (
          <p className="chat-widget__intro">Sin mensajes todavía.</p>
        ) : (
          messages.map((message) => <MessageBubble key={message.id} message={message} />)
        )}
      </div>

      {isActive ? (
        <form className="chat-widget__footer" onSubmit={handleSend}>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => onChatEnterKey(e, () => void submitMessage())}
            placeholder="Escribe un mensaje… (Enter para enviar)"
            rows={1}
            disabled={sending}
          />
          <Button type="submit" size="sm" disabled={sending || !draft.trim()}>
            Enviar
          </Button>
        </form>
      ) : showRatingPrompt ? (
        <div className="chat-widget__footer chat-widget__footer--rating">
          <p className="chat-widget__rating-label">¿Cómo valorarías la atención del agente?</p>
          <div className="chat-widget__stars" role="group" aria-label="Valoración del agente">
            {[1, 2, 3, 4, 5].map((value) => {
              const active = (ratingHover ?? Number(customerRating ?? 0)) >= value;
              return (
                <button
                  key={value}
                  type="button"
                  className={`chat-widget__star${active ? ' chat-widget__star--active' : ''}`}
                  disabled={ratingSubmitting}
                  aria-label={`${value} estrellas`}
                  onMouseEnter={() => setRatingHover(value)}
                  onMouseLeave={() => setRatingHover(null)}
                  onClick={() => void submitRating(String(value))}
                >
                  ★
                </button>
              );
            })}
          </div>
          <textarea
            className="chat-widget__rating-feedback"
            value={ratingFeedback}
            onChange={(e) => setRatingFeedback(e.target.value)}
            placeholder="Comentario opcional…"
            rows={2}
            disabled={ratingSubmitting}
          />
        </div>
      ) : sessionToken ? (
        <div className="chat-widget__footer chat-widget__footer--ended">
          <p className="chat-widget__ended-text">
            {showClosedBanner ? 'Esta conversación está cerrada' : 'Esta conversación ha finalizado'}
          </p>
          <Button type="button" size="sm" onClick={startNewConversation}>
            Nueva conversación
          </Button>
        </div>
      ) : null}
    </div>
  );
}
